/**
 * Helpers.js
 * 
 * This module provides utility functions for 3D geometry operations in the Eagle Eye Designer application.
 * It contains helper functions for:
 * 1. Creating wireframe visualizations for panels
 * 2. Extracting vertices from 3D objects
 * 3. Calculating edge directions for proper alignment
 * 4. Computing rotation quaternions for orientation adjustments
 * 
 * These geometric utility functions support panel placement and visualization
 * throughout the application.
 */

import * as THREE from 'three';
import { LINES } from '../Global/materials';
import { createEdgesWireframe } from "../Global/functions";

/**
 * Creates a wireframe outline for a panel
 * 
 * This function extracts the vertices of a panel, reorders them to form a proper
 * rectangular outline, and creates a wireframe visualization that's added as a
 * child to the panel. The wireframe is used for visual feedback during panel
 * placement and selection.
 * 
 * @param {THREE.Mesh} panel - The panel mesh to create a wireframe for
 */
export function createWireframe(panel) {
  // Get the vertices of the panel in world space
  const vertices = getVertices(panel);
  
  // Reorder vertices to ensure a rectangular outline
  // This handles the case where vertices from a PlaneGeometry aren't in the expected order
  const last = vertices.splice(2, 1);
  vertices.push(last[0]);
  
  // Create an array of points for each edge in the wireframe
  const edgePoints = [];
  for (let i = 0; i < vertices.length; i++) {
    // Calculate the index of the next vertex (wrapping around at the end)
    let index = (i + 1) % vertices.length;
    const point1 = vertices[i];
    const point2 = vertices[index];

    // Add both points to form a line segment
    edgePoints.push(point1);
    edgePoints.push(point2);
  }
  
  // Clone the red material for the wireframe
  const wireMat = LINES.redMaterial.clone();
  
  // Create a wireframe using the edge points and material
  const edgeWireframe = createEdgesWireframe(edgePoints, wireMat);
  
  // Make the wireframe transparent initially (will be made visible when selected)
  edgeWireframe.material.setValues({ transparent: true, opacity: 0 });
  
  // Set high render order to ensure the wireframe is visible on top of the panel
  edgeWireframe.renderOrder = 6;
  
  // Add the wireframe as a child of the panel for easier management
  panel.add(edgeWireframe);
}

/**
 * Extracts vertices from a 3D object in world space
 * 
 * This function takes a THREE.js object with geometry and extracts all of its
 * vertices, converting them from local object space to world space coordinates.
 * 
 * @param {THREE.Mesh} object - The 3D object to extract vertices from
 * @returns {Array<THREE.Vector3>} Array of vertices in world space
 */
export function getVertices(object) {
  const vertices = [];
  
  // Get the position attribute from the geometry
  const positionAttribute = object.geometry.attributes.position;
  
  // Get the number of vertices in the geometry
  const vertexCount = positionAttribute.count;
  
  // Extract each vertex and convert to world space
  for (let i = 0; i < vertexCount; i++) {
    // Create a new Vector3 from the position buffer attribute
    const vertex = new THREE.Vector3().fromBufferAttribute(positionAttribute, i);
    
    // Apply the object's world matrix to convert to world space
    vertex.applyMatrix4(object.matrixWorld);
    
    // Add the vertex to the result array
    vertices.push(vertex);
  }
  
  return vertices;
}

/**
 * Calculates the direction vector of a horizontal edge in a segment
 * 
 * This function finds a horizontal edge (where both vertices have approximately
 * the same z-coordinate) in the segment mesh and returns its direction vector.
 * This is used for proper alignment of panels with the segment's orientation.
 * 
 * @param {THREE.Mesh} segment - The segment mesh to analyze
 * @param {THREE.Vector3} normal - The normal vector of the segment plane
 * @returns {THREE.Vector3} Direction vector of a horizontal edge
 */
function getEdgeDirectionOriginal(segment, normal) {
  
  // Initialize edge direction vector
  const edgeDirection = new THREE.Vector3();
  
  // Get all vertices of the segment in world space
  const vertices = getVertices(segment);

  // to store pair of vertices most approximately horizontal
  let minDiff = Infinity;
  let minPair = [];

  // Loop through all vertex pairs to find a horizontal edge
  for (let i = 0; i < vertices.length; i++) {
    for (let j = i + 1; j < vertices.length; j++) {
      const diff = vertices[i].z - vertices[j].z;
      if (Math.abs(diff) < 1) {
        edgeDirection.subVectors(vertices[i], vertices[j]).normalize();
        const crossProduct = new THREE.Vector3().crossVectors(vertices[i], vertices[j]);
        if (crossProduct.dot(normal) < 0) {
          edgeDirection.negate();
        }
        return edgeDirection;
      }
    }
  }
  return edgeDirection;
}

export function getEdgeDirection(segment, normal, strategy = 'adaptive') {
  const edges = segment?.setBacks?.isSet ? segment?.setBacks?.edgesData : segment?.edges;
  
  if (edges && edges.length > 0) {
    
    // Step 1: Calculate Z-differences for all edges
    const edgeAnalysis = edges.map(edge => {
      const zDiff = Math.abs(edge.point1.z - edge.point2.z);
      const direction = new THREE.Vector3().subVectors(edge.point2, edge.point1).normalize();
      
      // Calculate alignment with cardinal directions (X and Y axes)
      const xAlignment = Math.abs(direction.dot(new THREE.Vector3(1, 0, 0))); // X-axis alignment
      const yAlignment = Math.abs(direction.dot(new THREE.Vector3(0, 1, 0))); // Y-axis alignment
      const cardinalAlignment = Math.max(xAlignment, yAlignment);
      
      return {
        edge,
        zDiff,
        direction,
        xAlignment,
        yAlignment,
        cardinalAlignment
      };
    });
    
    // Step 2: Check if Z-differences are meaningful
    const minZDiff = Math.min(...edgeAnalysis.map(e => e.zDiff));
    const maxZDiff = Math.max(...edgeAnalysis.map(e => e.zDiff));
    const zDiffRange = maxZDiff - minZDiff;
    
    let selectedEdge;
    
    // Step 3: Decide selection criteria based on Z-difference significance
    const Z_DIFF_THRESHOLD = 0.3; // If Z-range is less than 50cm, consider it "flat"
    
    if (zDiffRange < Z_DIFF_THRESHOLD) {
      // Case: Relatively flat roof - use cardinal direction alignment
      selectedEdge = edgeAnalysis.reduce((best, current) => {
        return current.cardinalAlignment > best.cardinalAlignment ? current : best;
      }).edge;
            
    } else {
      
      // Find all edges with minimum Z-difference
      const horizontalEdges = edgeAnalysis.filter(e => Math.abs(e.zDiff - minZDiff) < 0.01);
      
      if (horizontalEdges.length === 1) {
        selectedEdge = horizontalEdges[0].edge;
      } else {
        
        selectedEdge = horizontalEdges.reduce((best, current) => {
          return current.cardinalAlignment > best.cardinalAlignment ? current : best;
        }).edge;
      }
    }
    
    const direction = new THREE.Vector3().subVectors(selectedEdge.point2, selectedEdge.point1).normalize();
    return direction;
  }
  
  return getEdgeDirectionOriginal(segment, normal);
}

/**
 * Calculates the rotation quaternion to align a panel with a segment edge
 * 
 * This function computes the rotation needed to align the panel's local x-axis
 * with the direction of an edge in the segment. It returns both the sign of the
 * rotation (which affects offset calculations) and the rotation quaternion.
 * 
 * @param {THREE.Vector3} normal - The normal vector of the segment plane
 * @param {THREE.Vector3} direction - The direction vector of the segment edge
 * @param {THREE.Quaternion} quaternion - The initial quaternion of the panel
 * @returns {Array} Array containing [sign, rotationQuaternion]
 */
export function getRotation(normal, direction, quaternion) {
  // Get the panel's local x-axis after applying initial quaternion
  const planeEdgeDirection = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
  
  // Use the segment's normal as the rotation axis
  const rotationAxis = normal;
  
  // Calculate the angle between the panel's x-axis and the segment edge direction
  const angle = planeEdgeDirection.angleTo(direction);
  
  // Calculate cross product to determine rotation direction
  const crossProduct = new THREE.Vector3().crossVectors(planeEdgeDirection, direction);
  
  // Determine if rotation should be clockwise or counter-clockwise
  // This affects both the rotation and the offset calculation
  const sign = crossProduct.dot(rotationAxis) < 0 ? -1 : 1;
  
  // Create a quaternion representing the rotation around the normal vector
  const rotationQuaternion = new THREE.Quaternion().setFromAxisAngle(rotationAxis, sign * angle);

  // Return both the sign (for offset calculations) and the rotation quaternion
  return [sign, rotationQuaternion];
}